import type {
    SmriqChosenServiceSelection,
    SmriqLastCompletedServiceOption,
    SmriqLastCompletedServiceSelection,
} from '../types/smriq.types';
import {
    AUTO_MATCH_OPTION,
    NO_SERVICE_OPTION,
    serviceOptionIdFrom,
    serviceOptionValue,
    type SmriqLastCompletedServiceFormValues,
} from '../schemas/smriqLastCompletedService.schema';
import { isAutoMatch } from './isAutoMatch';

export type HeldServiceOption = Pick<SmriqChosenServiceSelection, 'service_option_id' | 'label'>;

const detailValues = (selection: SmriqLastCompletedServiceSelection) => ({
    completed_date: selection.completed_date ?? '',
    completed_mileage:
        selection.completed_mileage == null ? '' : String(selection.completed_mileage),
});

export const toFormValues = (
    selection: SmriqLastCompletedServiceSelection | null,
): SmriqLastCompletedServiceFormValues => {
    if (!selection) {
        return { service_option_id: NO_SERVICE_OPTION, completed_date: '', completed_mileage: '' };
    }
    const value = isAutoMatch(selection)
        ? AUTO_MATCH_OPTION
        : serviceOptionValue(selection.service_option_id);
    return { service_option_id: value, ...detailValues(selection) };
};

export const toSelection = (
    values: SmriqLastCompletedServiceFormValues,
    options: readonly SmriqLastCompletedServiceOption[],
    current: HeldServiceOption | null,
): SmriqLastCompletedServiceSelection | null => {
    if (values.service_option_id === NO_SERVICE_OPTION) return null;
    const details = {
        completed_date: values.completed_date.trim(),
        completed_mileage: Number(values.completed_mileage),
    };
    if (values.service_option_id === AUTO_MATCH_OPTION) return { mode: 'auto', ...details };
    const id = serviceOptionIdFrom(values.service_option_id);
    if (id === null) return null;
    const label =
        options.find((option) => option.id === id)?.label ??
        (current?.service_option_id === id ? current.label : undefined);
    if (label === undefined) return null;
    return { service_option_id: id, label, ...details };
};

export const earlierDateKey = (first: string, second: string | undefined): string =>
    second && second < first ? second : first;

export interface SelectOption {
    value: string;
    label: string;
}

/** "Not known" and "Auto match" first, then the vendor options by label; the saved choice stays
 *  selectable even when the current list no longer carries it. */
export const buildSelectOptions = (
    options: readonly SmriqLastCompletedServiceOption[],
    current: HeldServiceOption | null,
): SelectOption[] => {
    const entries: SelectOption[] = [
        { value: NO_SERVICE_OPTION, label: 'Not known' },
        { value: AUTO_MATCH_OPTION, label: 'Auto match' },
        ...options.map((option) => ({
            value: serviceOptionValue(option.id),
            label: option.label,
        })),
    ];
    if (!current) return entries;
    const currentValue = serviceOptionValue(current.service_option_id);
    if (!entries.some((entry) => entry.value === currentValue)) {
        entries.push({ value: currentValue, label: current.label });
    }
    return entries;
};
